// ============================================
// アニメーション状態管理
// ============================================

import type {
    ResultParticle,
    ShipSelectEffect,
    LaunchEffect,
    BoosterParticle
} from '../types/game-types';

// --- リザルト画面アニメーション用変数 ---
let _resultPhase: number = 0;
let _resultDisplayScore: number = 0;
let _resultEnemyIndex: number = 0;
let _resultRankShown: boolean = false;
let _resultParticles: ResultParticle[] = [];

// --- 機体選択画面アニメーション用変数 ---
let _shipSelectEffect: ShipSelectEffect | null = null;
let _launchEffect: LaunchEffect | null = null;

// --- ステージ開始演出用変数（自機登場） ---
let _stageStartTimer: number = 0;
let _playerEntryY: number = 0;
let _boosterAlpha: number = 255;
let _boosterParticles: BoosterParticle[] = [];

// --- ウェーブ開始演出用変数（操作可能なまま） ---
let _waveStartTimer: number = 0;
let _isWaveStarting: boolean = false;

// --- ボス関連変数 ---
let _bossWarning: boolean = false;
let _bossWarningTimer: number = 0;
let _lastBossWave: number = 0;
let _bossDefeatPause: number = 0;
let _bossPaused: boolean = false;

// --- レーンバランス用変数 ---
let _lastSpawnLane: number = -1;
let _sameLaneCount: number = 0;

// --- タイミング管理 ---
let _spawnTimer: number = 0;

// --- コンボアニメーション ---
let _lastAnimatedCombo: number = 0;  // 最後にアニメーションしたコンボ数
let _comboAnimTimer: number = 0;     // アニメーションタイマー

// --- サイドバーニア ---
let _sideBoosterLeftTimer: number = 0;   // 左サイドバーニアタイマー
let _sideBoosterRightTimer: number = 0;  // 右サイドバーニアタイマー
let _prevMoveDirection: number = 0;      // 前フレームの移動方向

// --- メインバーニア移動アニメーション ---
let _engineMoveDirection: number = 0;    // lerp前の移動方向（エンジンアニメーション用）

// --- パネルスライドアニメーション ---
let _panelSlideProgress: number = 1;     // 0=画面外, 1=表示完了

// ============================================
// Getters and Setters
// ============================================

// --- リザルト画面 ---
export const getResultPhase = (): number => _resultPhase;
export const setResultPhase = (val: number): void => {
    _resultPhase = val;
};

export const getResultDisplayScore = (): number => _resultDisplayScore;
export const setResultDisplayScore = (val: number): void => {
    _resultDisplayScore = val;
};

export const getResultEnemyIndex = (): number => _resultEnemyIndex;
export const setResultEnemyIndex = (val: number): void => {
    _resultEnemyIndex = val;
};

export const getResultRankShown = (): boolean => _resultRankShown;
export const setResultRankShown = (val: boolean): void => {
    _resultRankShown = val;
};

export const getResultParticles = (): ResultParticle[] => _resultParticles;
export const setResultParticles = (val: ResultParticle[]): void => {
    _resultParticles = val;
};

// --- 機体選択画面 ---
export const getShipSelectEffect = (): ShipSelectEffect | null => _shipSelectEffect;
export const setShipSelectEffect = (val: ShipSelectEffect | null): void => {
    _shipSelectEffect = val;
};

export const getLaunchEffect = (): LaunchEffect | null => _launchEffect;
export const setLaunchEffect = (val: LaunchEffect | null): void => {
    _launchEffect = val;
};

// --- ステージ開始演出 ---
export const getStageStartTimer = (): number => _stageStartTimer;
export const setStageStartTimer = (val: number): void => {
    _stageStartTimer = val;
};

export const getPlayerEntryY = (): number => _playerEntryY;
export const setPlayerEntryY = (val: number): void => {
    _playerEntryY = val;
};

export const getBoosterAlpha = (): number => _boosterAlpha;
export const setBoosterAlpha = (val: number): void => {
    _boosterAlpha = val;
};

export const getBoosterParticles = (): BoosterParticle[] => _boosterParticles;
export const setBoosterParticles = (val: BoosterParticle[]): void => {
    _boosterParticles = val;
};

// --- ウェーブ開始演出 ---
export const getWaveStartTimer = (): number => _waveStartTimer;
export const setWaveStartTimer = (val: number): void => {
    _waveStartTimer = val;
};

export const getIsWaveStarting = (): boolean => _isWaveStarting;
export const setIsWaveStarting = (val: boolean): void => {
    _isWaveStarting = val;
};

// --- ボス関連 ---
export const getBossWarning = (): boolean => _bossWarning;
export const setBossWarning = (val: boolean): void => {
    _bossWarning = val;
};

export const getBossWarningTimer = (): number => _bossWarningTimer;
export const setBossWarningTimer = (val: number): void => {
    _bossWarningTimer = val;
};

export const getLastBossWave = (): number => _lastBossWave;
export const setLastBossWave = (val: number): void => {
    _lastBossWave = val;
};

export const getBossDefeatPause = (): number => _bossDefeatPause;
export const setBossDefeatPause = (val: number): void => {
    _bossDefeatPause = val;
};

export const getBossPaused = (): boolean => _bossPaused;
export const setBossPaused = (val: boolean): void => {
    _bossPaused = val;
};

// --- レーンバランス ---
export const getLastSpawnLane = (): number => _lastSpawnLane;
export const setLastSpawnLane = (val: number): void => {
    _lastSpawnLane = val;
};

export const getSameLaneCount = (): number => _sameLaneCount;
export const setSameLaneCount = (val: number): void => {
    _sameLaneCount = val;
};

// --- タイミング管理 ---
export const getSpawnTimer = (): number => _spawnTimer;
export const setSpawnTimer = (val: number): void => {
    _spawnTimer = val;
};

// --- コンボアニメーション ---
export const getLastAnimatedCombo = (): number => _lastAnimatedCombo;
export const setLastAnimatedCombo = (val: number): void => {
    _lastAnimatedCombo = val;
};

export const getComboAnimTimer = (): number => _comboAnimTimer;
export const setComboAnimTimer = (val: number): void => {
    _comboAnimTimer = val;
};

// --- サイドバーニア ---
export const getSideBoosterLeftTimer = (): number => _sideBoosterLeftTimer;
export const setSideBoosterLeftTimer = (val: number): void => {
    _sideBoosterLeftTimer = val;
};

export const getSideBoosterRightTimer = (): number => _sideBoosterRightTimer;
export const setSideBoosterRightTimer = (val: number): void => {
    _sideBoosterRightTimer = val;
};

export const getPrevMoveDirection = (): number => _prevMoveDirection;
export const setPrevMoveDirection = (val: number): void => {
    _prevMoveDirection = val;
};

// --- メインバーニア移動アニメーション ---
export const getEngineMoveDirection = (): number => _engineMoveDirection;
export const setEngineMoveDirection = (val: number): void => {
    _engineMoveDirection = val;
};

// --- パネルスライドアニメーション ---
export const getPanelSlideProgress = (): number => _panelSlideProgress;
export const setPanelSlideProgress = (val: number): void => {
    _panelSlideProgress = val;
};
